import {
  CognitoIdentityProviderClient,
  InitiateAuthCommand,
  RevokeTokenCommand,
} from '@aws-sdk/client-cognito-identity-provider';

// ─── Cognito auth (single pre-provisioned user) ───────────────────────────────
// Talks to Cognito's public InitiateAuth endpoint directly from the browser
// (USER_PASSWORD_AUTH, public app client, no Hosted UI). The only file in the
// app that knows about Cognito. No sign-up / password-change flows by design —
// the one user is provisioned out-of-band (see CLAUDE.md "Admin user provisioning").

const REGION = process.env.REACT_APP_COGNITO_REGION ?? '';
const CLIENT_ID = process.env.REACT_APP_COGNITO_CLIENT_ID ?? '';

const STORAGE_KEY = 'nospmah.auth';

// Refresh this long before the ID token actually expires.
export const REFRESH_MARGIN_MS = 5 * 60 * 1000;

export interface AuthSession {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  email: string;
  expiresAt: number; // epoch ms, from the ID token's exp claim
}

let client: CognitoIdentityProviderClient | null = null;

const getClient = (): CognitoIdentityProviderClient => {
  if (!REGION || !CLIENT_ID) {
    throw new Error('Cognito is not configured (REACT_APP_COGNITO_REGION / REACT_APP_COGNITO_CLIENT_ID).');
  }
  if (!client) {
    client = new CognitoIdentityProviderClient({ region: REGION });
  }
  return client;
};

// Decodes (does NOT verify) a JWT payload. The browser isn't a trust boundary —
// API Gateway's Cognito authorizer verifies signatures server-side.
const decodeJwtPayload = (token: string): Record<string, unknown> => {
  const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
  return JSON.parse(atob(padded));
};

const toSession = (idToken: string, accessToken: string, refreshToken: string): AuthSession => {
  const claims = decodeJwtPayload(idToken);
  return {
    idToken,
    accessToken,
    refreshToken,
    email: String(claims.email ?? ''),
    expiresAt: Number(claims.exp) * 1000,
  };
};

const saveSession = (session: AuthSession): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    // storage unavailable (private mode etc.) — session lives only as long as the page
  }
};

const clearSession = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};

const friendlyError = (err: unknown): Error => {
  const name = (err as { name?: string })?.name ?? '';
  switch (name) {
    case 'NotAuthorizedException':
    case 'UserNotFoundException':
      return new Error('Incorrect email or password.');
    case 'PasswordResetRequiredException':
      return new Error('Password reset required — reset it with the admin provisioning commands.');
    case 'TooManyRequestsException':
    case 'LimitExceededException':
      return new Error('Too many attempts. Wait a few minutes and try again.');
    default:
      return err instanceof Error ? err : new Error('Sign-in failed.');
  }
};

export const signIn = async (email: string, password: string): Promise<AuthSession> => {
  let response;
  try {
    response = await getClient().send(new InitiateAuthCommand({
      AuthFlow: 'USER_PASSWORD_AUTH',
      ClientId: CLIENT_ID,
      AuthParameters: { USERNAME: email.trim(), PASSWORD: password },
    }));
  } catch (err) {
    throw friendlyError(err);
  }

  // Any challenge (e.g. NEW_PASSWORD_REQUIRED) means the user wasn't provisioned
  // with a permanent password. There's deliberately no in-app flow for that.
  if (response.ChallengeName) {
    throw new Error(`Account needs admin setup (${response.ChallengeName}) — see the provisioning commands.`);
  }

  const result = response.AuthenticationResult;
  if (!result?.IdToken || !result.AccessToken || !result.RefreshToken) {
    throw new Error('Sign-in failed: incomplete response from Cognito.');
  }

  const session = toSession(result.IdToken, result.AccessToken, result.RefreshToken);
  saveSession(session);
  return session;
};

export const refresh = async (session: AuthSession): Promise<AuthSession> => {
  const response = await getClient().send(new InitiateAuthCommand({
    AuthFlow: 'REFRESH_TOKEN_AUTH',
    ClientId: CLIENT_ID,
    AuthParameters: { REFRESH_TOKEN: session.refreshToken },
  }));

  const result = response.AuthenticationResult;
  if (!result?.IdToken || !result.AccessToken) {
    throw new Error('Session refresh failed.');
  }

  // Cognito only returns a new refresh token when rotation is enabled — keep the old one otherwise.
  const refreshed = toSession(result.IdToken, result.AccessToken, result.RefreshToken ?? session.refreshToken);
  saveSession(refreshed);
  return refreshed;
};

export const signOut = async (session: AuthSession | null): Promise<void> => {
  clearSession();
  if (!session) return;
  try {
    // Invalidates the refresh token server-side. Best effort — local sign-out already happened.
    await getClient().send(new RevokeTokenCommand({ ClientId: CLIENT_ID, Token: session.refreshToken }));
  } catch {
    // offline / already revoked — nothing to do
  }
};

// Returns the stored session, refreshing it first if it's expired or about to be.
// Resolves null (and clears storage) when there's no usable session.
export const getSession = async (): Promise<AuthSession | null> => {
  let stored: AuthSession | null = null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    stored = raw ? (JSON.parse(raw) as AuthSession) : null;
  } catch {
    stored = null;
  }
  if (!stored?.refreshToken) return null;

  if (stored.expiresAt - Date.now() > REFRESH_MARGIN_MS) return stored;

  try {
    return await refresh(stored);
  } catch {
    clearSession();
    return null;
  }
};
